/**
 * Sends edge-function calls for routed functions to core-functions on Cloud Run
 * instead of Supabase, resolved once per page load. Which functions are routed
 * comes from `port-manifest.json` through `routed-functions.generated.ts`:
 * `routed-dev` ones only for a browser that opted in before the page loaded,
 * `routed-all` ones for every browser. Only the dev deployment has a
 * core-functions origin, so every other build leaves all calls on Supabase.
 * See docs/plans/2026-10-08-feat-edge-functions-to-node-migration-plan.md (MF).
 */

import { DEV_GCP_PROJECT_NUMBER } from './pilot-api.constants';
import { ROUTED_FUNCTIONS_EVERYONE, ROUTED_FUNCTIONS_OPT_IN } from './routed-functions.generated';

const OPT_IN_KEY = 'pp.pilot.coreFunctions';
// The deployed dev site builds in Vite mode `production`; only this marker
// tells it from staging and production (as in `pilot-api.ts`).
const PILOT_DEPLOYMENT = 'dev';
const FUNCTIONS_PREFIX = '/functions/v1/';

const buildEnv = import.meta.env as Record<string, string | undefined>;

// The browser sends the user's access token there, so it is only ever the dev
// core-functions service's deterministic Cloud Run origin, never configurable.
const CORE_FUNCTIONS_HOST =
  DEV_GCP_PROJECT_NUMBER === null
    ? null
    : `core-functions-${DEV_GCP_PROJECT_NUMBER}.australia-southeast1.run.app`;
const coreFunctionsOrigin: string | null =
  buildEnv.VITE_PILOT_DEPLOYMENT === PILOT_DEPLOYMENT && CORE_FUNCTIONS_HOST !== null
    ? `https://${CORE_FUNCTIONS_HOST}`
    : null;

function parseOrigin(value: string | undefined): string | null {
  try {
    return value ? new URL(value).origin : null;
  } catch {
    return null;
  }
}

const supabaseOrigin = parseOrigin(buildEnv.VITE_SUPABASE_URL);

// Storage can be missing or throw (privacy mode, blocked site data): that is off.
function hasOptedIn(): boolean {
  try {
    return globalThis.localStorage.getItem(OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
}

/** The functions this page load sends to core-functions; empty when it has no origin. */
export const routedFunctionNames: ReadonlySet<string> =
  coreFunctionsOrigin === null || supabaseOrigin === null
    ? new Set()
    : new Set([...ROUTED_FUNCTIONS_EVERYONE, ...(hasOptedIn() ? ROUTED_FUNCTIONS_OPT_IN : [])]);

function routedUrl(url: string): string | null {
  if (coreFunctionsOrigin === null || routedFunctionNames.size === 0) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.origin !== supabaseOrigin || !parsed.pathname.startsWith(FUNCTIONS_PREFIX)) {
    return null;
  }
  const name = parsed.pathname.slice(FUNCTIONS_PREFIX.length).split('/')[0] ?? '';
  if (!routedFunctionNames.has(name)) {
    return null;
  }
  return `${coreFunctionsOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * The `fetch` arguments with a routed function's Supabase URL moved to
 * core-functions; any other request gets its arguments back as they were. The
 * path, query, method, headers and body stay as they are: both runtimes serve
 * the same `/functions/v1/<name>` contract. A `Request` input is never
 * rerouted: copying one to a new URL turns its body into a stream that
 * browsers refuse without `duplex`, and supabase-js passes a URL string.
 */
export function routeFunctionRequest(...args: Parameters<typeof fetch>): Parameters<typeof fetch> {
  const [input, ...rest] = args;
  if (input instanceof Request) {
    return args;
  }
  const target = routedUrl(input instanceof URL ? input.href : input);
  return target === null ? args : [target, ...rest];
}
